import { useCallback, useEffect, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass, mfgInlineInputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { updateManufacturingRecipe } from "@/lib/manufacturingClient";
import {
  mergeVariantConfigIntoTags,
  parseVariantConfigFromTags,
  type VariantConfig,
  type VariantTransformRule,
} from "@/lib/manufacturing/variantConfig";
import { SlotLabelsMultiCombobox } from "./SlotLabelCombobox";

type Mode = "delta" | "transform";

function defaultRules(): VariantTransformRule[] {
  return [{ slots: [], fromNominal: "2x4", toNominal: "2x6" }];
}

function rulesFromConfig(config: VariantConfig): VariantTransformRule[] {
  if ((config.mode === "transform" || config.mode === "hybrid") && config.rules.length > 0) {
    return config.rules;
  }
  return defaultRules();
}

function modeFromConfig(config: VariantConfig): Mode {
  return config.mode === "transform" || config.mode === "hybrid" ? "transform" : "delta";
}

export function ManufacturingModelsVariantTransformEditor({
  recipeId,
  tags,
  orgSlotLabels,
  readOnly = false,
  onSaved,
  onError,
}: {
  recipeId: string;
  tags: readonly string[];
  orgSlotLabels: readonly string[];
  readOnly?: boolean;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const parsed = parseVariantConfigFromTags(tags);
  const [mode, setMode] = useState<Mode>(modeFromConfig(parsed));
  const [rules, setRules] = useState<VariantTransformRule[]>(() => rulesFromConfig(parsed));
  const [saveBusy, setSaveBusy] = useState(false);

  useEffect(() => {
    const next = parseVariantConfigFromTags(tags);
    setMode(modeFromConfig(next));
    setRules(rulesFromConfig(next));
  }, [tags]);

  const patchRule = useCallback((index: number, patch: Partial<VariantTransformRule>) => {
    setRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }, []);

  const addRule = useCallback(() => {
    setRules((prev) => [...prev, ...defaultRules()]);
  }, []);

  const removeRule = useCallback((index: number) => {
    setRules((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }, []);

  async function onSave() {
    const config: VariantConfig =
      mode === "transform"
        ? {
            mode: "transform",
            rules: rules
              .map((r) => ({
                slots: r.slots.map((s) => s.trim()).filter(Boolean),
                fromNominal: r.fromNominal.trim().toLowerCase(),
                toNominal: r.toNominal.trim().toLowerCase(),
              }))
              .filter((r) => r.slots.length > 0 && r.fromNominal && r.toNominal),
          }
        : { mode: "delta" };

    if (config.mode === "transform" && config.rules.length === 0) {
      onError("Add at least one transform rule with slots, from, and to nominal sizes.");
      return;
    }

    setSaveBusy(true);
    const res = await updateManufacturingRecipe({
      recipe_id: recipeId,
      tags: mergeVariantConfigIntoTags(tags, config),
    });
    setSaveBusy(false);
    if (!res.ok) {
      onError(res.error);
      return;
    }
    onSaved();
  }

  return (
    <SectionCard
      title="Variant resolution mode"
      description={
        <>
          <strong>Delta</strong> — variant BOM lines replace parent slots (today&apos;s behavior).{" "}
          <strong>Transform</strong> — rewrite parent slot materials in place (e.g. 2×4 → 2×6 at
          same length); parent size drives qty and length. Link on each parent with{" "}
          <code>replaces_slot_labels</code> as usual.
        </>
      }
    >
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name={`variant-mode-${recipeId}`}
            checked={mode === "delta"}
            disabled={readOnly}
            onChange={() => setMode("delta")}
          />
          Delta BOM
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name={`variant-mode-${recipeId}`}
            checked={mode === "transform"}
            disabled={readOnly}
            onChange={() => setMode("transform")}
          />
          Transform rules
        </label>
      </div>

      {mode === "transform" ? (
        <div className="mt-4 space-y-3">
          {rules.map((rule, index) => (
            <div
              key={index}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-2 dark:border-zinc-700"
            >
              <SlotLabelsMultiCombobox
                value={rule.slots.join(", ")}
                suggestions={orgSlotLabels}
                placeholder="Slots (comma-sep)"
                disabled={readOnly}
                className={`${mfgInlineInputClass} min-w-[160px]`}
                onChange={(v) =>
                  patchRule(index, {
                    slots: v
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
              <input
                className={`${mfgInlineInputClass} w-28`}
                placeholder="size, code, or name"
                value={rule.fromNominal}
                disabled={readOnly}
                onChange={(e) => patchRule(index, { fromNominal: e.target.value })}
              />
              <span className="text-xs text-gray-500">→</span>
              <input
                className={`${mfgInlineInputClass} w-28`}
                placeholder="size, code, or name"
                value={rule.toNominal}
                disabled={readOnly}
                onChange={(e) => patchRule(index, { toNominal: e.target.value })}
              />
              {!readOnly && rules.length > 1 ? (
                <button type="button" className={ghostBtnClass} onClick={() => removeRule(index)}>
                  Remove
                </button>
              ) : null}
            </div>
          ))}
          {!readOnly ? (
            <button type="button" className={ghostBtnClass} onClick={addRule}>
              + Rule
            </button>
          ) : null}
        </div>
      ) : null}

      {!readOnly ? (
        <div className="mt-4 flex justify-end">
          <button type="button" className={mfgPrimaryBtnClass} disabled={saveBusy} onClick={() => void onSave()}>
            {saveBusy ? "Saving…" : "Save variant mode"}
          </button>
        </div>
      ) : null}
    </SectionCard>
  );
}
